import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faMapMarkerAlt, faUser, faVideo, faTimes } from '@fortawesome/free-solid-svg-icons';

interface EventModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (eventData: any) => void;
    event?: any;
    mode: 'create' | 'edit' | 'view';
}

const EventModal: React.FC<EventModalProps> = ({ show, onHide, onSubmit, event, mode }) => {
    const [formData, setFormData] = useState({
        title: event?.title || '',
        description: event?.description || '',
        event_type: event?.event_type || 'appointment',
        priority: event?.priority || 'medium',
        status: event?.status || 'scheduled',
        start_datetime: event?.start_datetime ? event.start_datetime.slice(0, 16) : '',
        end_datetime: event?.end_datetime ? event.end_datetime.slice(0, 16) : '',
        is_all_day: event?.is_all_day || false,
        location: event?.location || '',
        meeting_link: event?.meeting_link || '',
        notes: event?.notes || ''
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
        onHide();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));
    };

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {mode === 'create' ? 'Create New Event' : mode === 'edit' ? 'Edit Event' : 'Event Details'}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-8">
                                    <div className="mb-3">
                                        <label className="form-label">Event Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            required
                                            disabled={mode === 'view'}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Event Type</label>
                                        <select
                                            className="form-select"
                                            name="event_type"
                                            value={formData.event_type}
                                            onChange={handleChange}
                                            disabled={mode === 'view'}
                                        >
                                            <option value="appointment">Appointment</option>
                                            <option value="meeting">Meeting</option>
                                            <option value="task">Task</option>
                                            <option value="reminder">Reminder</option>
                                            <option value="deadline">Deadline</option>
                                            <option value="holiday">Holiday</option>
                                            <option value="training">Training</option>
                                            <option value="maintenance">Maintenance</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    disabled={mode === 'view'}
                                />
                            </div>

                            <div className="row">
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Priority</label>
                                        <select
                                            className="form-select"
                                            name="priority"
                                            value={formData.priority}
                                            onChange={handleChange}
                                            disabled={mode === 'view'}
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            disabled={mode === 'view'}
                                        >
                                            <option value="scheduled">Scheduled</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="postponed">Postponed</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="is_all_day"
                                                checked={formData.is_all_day}
                                                onChange={handleChange}
                                                disabled={mode === 'view'}
                                            />
                                            <label className="form-check-label">All Day Event</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <FontAwesomeIcon icon={faClock} className="me-1" />
                                            Start Date & Time
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            name="start_datetime"
                                            value={formData.start_datetime}
                                            onChange={handleChange}
                                            required
                                            disabled={mode === 'view'}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <FontAwesomeIcon icon={faClock} className="me-1" />
                                            End Date & Time
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            name="end_datetime"
                                            value={formData.end_datetime}
                                            onChange={handleChange}
                                            required
                                            disabled={mode === 'view'}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            placeholder="Enter location"
                                            disabled={mode === 'view'}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <FontAwesomeIcon icon={faVideo} className="me-1" />
                                            Meeting Link <span className="text-muted">(Optional)</span>
                                        </label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            name="meeting_link"
                                            value={formData.meeting_link}
                                            onChange={handleChange}
                                            placeholder="https://meet.google.com/..."
                                            disabled={mode === 'view'}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Notes</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="Additional notes or details"
                                    disabled={mode === 'view'}
                                />
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>
                            {mode === 'view' ? 'Close' : 'Cancel'}
                        </button>
                        {mode !== 'view' && (
                            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                                {mode === 'create' ? 'Create Event' : 'Update Event'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventModal;
